import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import momentHelper from 'scoreboard/components/moment-helper'
import formatValue from 'scoreboard/components/format-value'
import DatasetField from 'scoreboard/datasets/model/dataset-field'
import calendars from 'scoreboard/components/calendars'
import KeyDisplay from 'scoreboard/datasets/key-display'
import highlight from 'scoreboard/components/highlight'
import dayOfTheWeek from 'scoreboard/components/days-of-the-week'
import { getBreakdownPeriodLabel }
	from 'scoreboard/components/designer/breakdown-period-options'
import monthOfTheYear from 'scoreboard/components/months-of-the-year'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import globals from 'scoreboard/components/globals'
import RelativeOptions from 'scoreboard/components/relative-options'
const relativeLabel = RelativeOptions.prototype.label
import unwrapModel from 'scoreboard/components/unwrap-model'
import Dataset from 'scoreboard/datasets/model/dataset'
const { ALL_TIME } = Dataset
import $ from 'jquery'
import getRollupIsAnyFilters from 'scoreboard/datasets/filters/get-rollup-is-any-filters'
import constants from 'scoreboard/components/constants'
import { getNumericFormatValueOptions } from 'scoreboard/datasets/filters/filter-numeric-values'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import isGeographicOrRollup from '../is-geographic-or-rollup'
import beginsEndsWithOperators from 'scoreboard/datasets/filters/begins-ends-with-operators'
import DataType from 'scoreboard/datasets/model/data-type'
const { TEXT, YES_NO, DATE, DATE_TIME, CALENDAR_PERIOD, TIME, CLUSTER, NUMBER } = DataType

/**
 * Returns a natural language (e.g. English) description of a filter.  The
 * returned value is HTML.  Also has the side-effect of setting
 * filter.valid = false if an invalid filter is found (i.e. it references an
 * invalid calendar period or field range)
 *
 * @param {Dataset} dataset
 * 		the dataset
 * @param {Object} filter
 * 		the filter
 * @param {Object} field
 * 		the field upon which the filter operates
 * @param {Object} treeItemsById
 * 		 a hash of tree IDs to tree items
 *
 * @param {Object} options
 *
 * @param {boolean} [options.resolvePeriodDeltas] if true, will turn relative
 * periods (like "2 Periods Earlier") into actual periods relative to globals.periodId
 * (like "December 2017")
 *
 * @param {boolean} [options.hiddenFilterType] if true, will construct the text in
 * a manner appropriate for a filter widget that isn't displaying the operator.
 * For example, if the operator is shown, a "between" filter's text might be
 * "15 and 20", because it will then display to the user as "between 15 and 20".
 * However, if the operator is not shown, hiddenFilterType should be set to true
 * so the text will be "15 to 20", which makes more sense when "between" isn't shown.
 *
 * @param {boolean} [options.showBuiltInRollupTreeGroupLabel=false] Pass true to display
 * the rollup tree group label instead of the tree item's label. (#34846)
 *
 */
function filterText(dataset, filter, field, treeItemsById, options = {}) {
	field = _.get(field, 'attributes', field)
	if (_.isNil(_.get(filter, 'operator'))) {
		return ''
	}

	// #44510 add the (not matching) suffix if needed
	const notMatchingSuffix = filter.matching === 'none' ?
		` (${i18n.get('datasets.filters.notMatching.lowerCase')})` : ''
	let filterText = commonFilterText(dataset, filter, field)
	if (filterText) {
		return appendNotMatchingSuffix(filterText, notMatchingSuffix)
	}

	var dataType = dataset ? unwrapModel(dataset.getUnderlyingField(field)).dataType : field.dataType
	if (dataType === YES_NO) {
		filterText = yesNoFilterText(filter, field)
	}
	if (isNumericDataType(dataType)) {
		filterText = numberFilterText(dataset, filter, field)
	}
	if (dataType === TEXT || dataType === CLUSTER) {
		filterText = textFilterText(dataset, filter, field)
	}
	if (dataType === DATE || dataType === DATE_TIME || dataType === TIME ||
		dataType === CALENDAR_PERIOD) {
		const calendarLabel = getCalendarLabel(filter)
		filterText = dateFilterText(filter, field, null, null, null, options) +
			calendarLabel
	}
	if (isGeographicOrRollup(dataType)) {
		filterText = rollupFilterText(dataset, filter, field, treeItemsById,
			null, null, options)
	}

	return appendNotMatchingSuffix(filterText, notMatchingSuffix)
}

function appendNotMatchingSuffix(filterText, notMatchingSuffix) {
	const filterText$ = $($.parseHTML(`<span>${filterText}</span>`))
	filterText$.append(notMatchingSuffix)
	return filterText$.html()
}

/**
 * handles filter descriptions common to multiple types of fields
 *
 * @param {Dataset} dataset
 */
function commonFilterText(dataset, filter, field) {
	if (!_.isNil(filter.valid) && !filter.valid) {
		return i18n.get('datasets.filters.error')
	}

	if (filter.operator === 'contains-any' || filter.operator === 'contains-all' ||
		filter.operator === 'does-not-contain-any' || filter.operator === 'does-not-contain-all') {
		let values = (filter.values || []).concat(filter.equations || [])
		return containsMessage(filter.operator, values, filter.filterOn)
	}

	if (filter.operator === 'begins-with') {
		return beginsEndsWith('report.filter.pretty.text.beginsWith', filter)
	}
	if (filter.operator === 'ends-with') {
		return beginsEndsWith('report.filter.pretty.text.endsWith', filter)
	}
	if (filter.operator === 'does-not-begin-with') {
		return beginsEndsWith('report.filter.pretty.text.doesNotBeginWith', filter)
	}
	if (filter.operator === 'does-not-end-with') {
		return beginsEndsWith('report.filter.pretty.text.doesNotEndWith', filter)
	}

	if (filter.operator === 'is-blank') {
		return i18n.get('datasets.filters.pretty.text.is', highlight(i18n.get('datasets.filters.pretty.blank')))
	}
	if (filter.operator === 'not-blank') {
		return i18n.get('datasets.filters.pretty.isNot', highlight(i18n.get('datasets.filters.pretty.blank')))
	}

	if (filter.operator === 'not-in-any-range') {
		if (field.dataType === TEXT) {
			return i18n.get('datasets.filters.pretty.notInAnyCategory')
		} else {
			return i18n.get('datasets.filters.pretty.notInAnyRange')
		}
	}
	if (filter.operator === 'in-the-range') {
		let values = rangeValues(dataset, filter, field)
		return valuesText('datasets.filters.pretty.date.inAny.singular',
			'datasets.filters.pretty.date.inAny', values)
	}
}

/**
 * Returns text appropriate for a begins with or ends with filter
 *
 * @param {String} key the i18n key used to prefix the filter text (e.g. 'begins with'
 * or 'ends with')
 * @param {Object} filter
 * @returns {String} the filter text
 */
function beginsEndsWith(key, filter) {
	let value
	if (filter.updateType1 === 'calculated') {
		value = highlight(_.first(filter.equations))
	} else if (!_.isNil(filter.value)) {
		value = filter.value
	} else {
		value = _.first(filter.values)
	}
	return filterOnCheck(filter.filterOn, i18n.get(key, value))
}

/**
 * @param {Dataset} dataset
 */
function rangeValues (dataset, filter, field) {
	// wrap up in a Model to ease finding ranges by ID
	let underlyingField = new DatasetField(dataset.getUnderlyingField(field))

	var values = _.chain(filter.rangeIds)
		.map(function(rangeId) {
			let range = underlyingField.ranges.get(rangeId)
			if (!range) {
				// it's gross to have a side-effect like this, but the
				// alternative would be more code
				filter.valid = false
				let type = isNumericDataType(underlyingField.get('dataType')) ? 'numeric' : TEXT
				return i18n.get(`datasets.fields.ranges.${type}.deleted`)
			}
			return range.get('label')
		})
		.sortBy(function(rangeLabel) {
			return rangeLabel.toUpperCase()
		})
		.value()

	return values
}

// handles filter descriptions for number fields
function numberFilterText(dataset, filter, field, singleArgCode, doubleArgCode) {
	field = _.get(field, 'attributes') || field

	var value1 = getNumberValue(filter, field, 1)

	if (filter.operator === 'is-any' || filter.operator === 'is-not-any') {
		return textFilterText(dataset, filter, field)
	}

	if (_.isNil(value1) || value1 === '') {
		return null
	}

	if (filter.operator === 'less-than' ||
		filter.operator === 'character-length-less-than') {
		return i18n.get(singleArgCode || 'datasets.filters.pretty.numeric.lessThan', value1)
	}
	if (filter.operator === 'less-than-or-equals') {
		return i18n.get(singleArgCode || 'datasets.filters.pretty.numeric.lessThanOrEquals', value1)
	}
	if (filter.operator === 'greater-than' ||
		filter.operator === 'character-length-greater-than') {
		return i18n.get(singleArgCode || 'datasets.filters.pretty.numeric.greaterThan', value1)
	}
	if (filter.operator === 'greater-than-or-equals') {
		return i18n.get(singleArgCode || 'datasets.filters.pretty.numeric.greaterThanOrEquals', value1)
	}

	if (filter.operator === 'between-number' ||
		filter.operator === 'character-length-between') {
		var value2 = getNumberValue(filter, field, 2)
		var firstValue = '<span class="filter-group">' + highlight(value1) + '</span>'
		var secondValue = '<span class="filter-group">' + highlight(value2) + '</span>'
		return i18n.get(doubleArgCode || 'datasets.filters.pretty.numeric.between', firstValue, secondValue)
	}
}

/**
 * Based on the update type, returns either the value or equation for a number
 * filter.
 */
function getNumberValue(filter, field, num) {
	if (filter[`updateType${num}`] === 'calculated') {
		return filter[`equation${num}`]
	} else {
		const options = {
			dataType: field.dataType,
			value: filter[`value${num}`],
			shorten: !field.primaryKey,
			precision: field.precision
		}
		if (isNumericDataType(field.dataType)) {
			_.extend(options, getNumericFormatValueOptions(options.value, field))
		}
		// #55465 for yes/no fields with an operator that uses numeric
		// value(s), don't try format the value using the YES_NO format
		// (which can result in a blank value)
		if (field.dataType === YES_NO) {
			options.dataType = NUMBER
		}
		return formatValue(options)
	}
}

// handles filter descriptions for yes/no fields
function yesNoFilterText(filter, field, showOperator = true) {
	if (filter.operator === 'is-any' || filter.operator === 'is-not-any') {
		// return i18n.get('datasets.filters.pretty.isAny')
		let values = (filter.values || []).map(val => {
			// #53701 lowercase the value when building the message code
			return i18n.get('common.' + (val === Dataset.BLANK ? 'blank' : _.lowerCase(val)))
		})

		let singleKey, multipleKey
		if (showOperator) {
			({ singleKey, multipleKey } = keysForIsAny(filter.operator))
		} else {
			singleKey = 'common.identity'
			multipleKey = 'common.or2'
		}
 		return valuesText(singleKey, multipleKey, values)
	}

	if (filter.operator === 'equals-equation') {
		if (showOperator) {
			return i18n.get('datasets.filters.pretty.numeric.equals', '<span class="filter-group">' + highlight(filter.equations[0]) + '</span>')
		} else {
			if (_.isEmpty(filter.equations)) {
				return null
			}

			return '<span class="filter-group">' + highlight(filter.equations[0]) + '</span>'
		}
	}
}

// handles filter descriptions for text fields
function textFilterText(dataset, filter, field, options = {}) {
	let { singleKey, multipleKey } = _.extend({}, keysForIsAny(filter.operator), options)
	const displayValues = (filter.values || []).map(value => {
		// #35528 don't display raw filter values for percent
		if (field.dataType) {
			if (field.dataType === CLUSTER) {
				return dataset.getClusterLabel(field, value)
			}
			const options = {
				dataType: field.dataType,
				value
			}
			if (isNumericDataType(field.dataType)) {
				_.extend(options, getNumericFormatValueOptions(value, field))
			}
			// #55465 if the field's dataType is YES_NO, don't format the value
			// because it can result in a blank value. Just display the value
			// as-is from the filter (this is applicable for operators on a
			// yes-no field that use text badges, ex. "character length is any ...")
			if (field.dataType === YES_NO) {
				return value
			}
			return formatValue(options)
		}

		return value
	})
	let values = displayValues.concat(filter.equations || [])

	return valuesText(singleKey, multipleKey, values)
}

/**
 * Returns the singleKey and multipleKey values that should be provided to
 * valuesText based on an operator
 */
function keysForIsAny(operator) {
	let singleKey, multipleKey
	if (operator === 'is-any' || operator === 'is') {
		singleKey = 'report.filter.pretty.numeric.equals'
		multipleKey = 'datasets.filters.pretty.text.isAny'
	}
	if (operator === 'is-not-any') {
		singleKey = 'datasets.filters.pretty.text.isNotAny1'
		multipleKey = 'datasets.filters.pretty.text.isNotAny'
	}
	return { singleKey, multipleKey }
}

const NO_APP_CALENDAR = [
	'in-month-of-the-year', 'on-day-of-the-week', 'on-any-date', 'in-any-period', 'in-any-period-index', 'is-period']

function dateFilterText(filter, field, singularKey, multipleAndKey, multipleOrKey, options) {
	// If the calendarId is All Time, no need to look any further
	// #19902 unless it's an operator that doesn't use the app calendar; in that
	// case, the calendarId is probably a remnant from when the filter was
	// previously using an operator that DID use the app calendar
	if (filter.calendarId == ALL_TIME && !_.includes(NO_APP_CALENDAR, filter.operator)) {
		return i18n.get('common.allTime')
	}

	var key
	if (filter.operator === 'before') {
		key = singularKey || 'datasets.filters.pretty.date.before'
	}
	if (filter.operator === 'after') {
		key = singularKey || 'datasets.filters.pretty.date.after'
	}
	if (filter.operator === 'between-date-time') {
		key = multipleAndKey || 'datasets.filters.pretty.numeric.between'
	}
	if (filter.operator === 'not-in-any-period') {
		return i18n.get('datasets.filters.pretty.notInAnyPeriod')
	}
	if (filter.operator === 'in-period') {
		key = singularKey || 'datasets.filters.pretty.date.inAny.singular'
	}

	if (key) {
		var value1 = getDateValue(filter, field, 1, options)

		if (!value1) {
			return null
		}

		if (filter.operator === 'between-date-time') {
			var value2 = getDateValue(filter, field, 2, options)

			if (!value2) {
				return null
			}

			return i18n.get(key,
				'<span class="filter-group">' + highlight(value1) + '</span>',
				'<span class="filter-group">' + highlight(value2) + '</span>')
		}

		if (_.includes(['before', 'after'], filter.operator)) {
			value1 = highlight(value1)
		}

		return i18n.get(key, value1, value2)
	}

	if (filter.operator === 'in-any-period' || filter.operator === 'in-any-period-index' ||
		filter.operator === 'is-period') {
		let periodLabels
		if (filter.operator === 'in-any-period' || filter.operator === 'is-period') {
			periodLabels = _.map(filter.periodIds, function(periodId) {
				return periodLabel(filter, periodId)
			})
		} else {
			periodLabels = _.map(filter.periodIndexes, index => {
				let groupCal = calendars.calendarsById[filter.groupCalendarId]
				let breakdownCal = calendars.calendarsById[filter.breakdownCalendarId]
				return getBreakdownPeriodLabel({
					index: parseInt(index),
					groupCalendarType: _.get(groupCal, 'type'),
					breakdownCalendarType: _.get(breakdownCal, 'type')
				})
			})
		}

		if (_.isEmpty(periodLabels)) {
			return null
		}

		if (filter.operator === 'is-period') {
			return valuesText(singularKey || 'datasets.filters.pretty.date.isAny.singular',
				multipleOrKey || 'datasets.filters.pretty.date.isAny', periodLabels)
		}

		return valuesText(singularKey || 'datasets.filters.pretty.date.inAny.singular',
			multipleOrKey || 'datasets.filters.pretty.date.inAny', periodLabels)
	}

	if (filter.operator === 'on-any-date') {
		var labels = _.map(filter.dates, function(date) {
			return momentHelper.formatDate(date)
		})

		let values = (labels || []).concat(filter.equations || [])

		if (_.isEmpty(values)) {
			return null
		}

		return valuesText(singularKey || 'datasets.filters.pretty.date.on.singular',
			multipleOrKey || 'datasets.filters.pretty.date.on', values)
	}

	if (filter.operator === 'at-any-time') {
		const timeFormat = momentHelper.getTimeFormat(field.timeDisplayType)
		var labels = _.map(filter.times, time => {
			return formatValue({
				value: time,
				dataType: TIME,
				timeFormat
			})
		})

		let values = (labels || []).concat(filter.equations || [])

		if (_.isEmpty(values)) {
			return null
		}

		return valuesText(singularKey || 'datasets.filters.pretty.time.on.singular',
			multipleOrKey || 'datasets.filters.pretty.time.on', values)
	}

	if (filter.operator === 'on-day-of-the-week') {
		let labels = _.map(filter.days, (day) => dayOfTheWeek()[day])

		if (_.isEmpty(labels)) {
			return null
		}

		return valuesText(singularKey || 'datasets.filters.pretty.date.on.singular',
			multipleOrKey || 'datasets.filters.pretty.date.on', labels)
	}

	if (filter.operator === 'in-month-of-the-year') {
		let labels = _.map(filter.months, (month) => monthOfTheYear()[month])

		if (_.isEmpty(labels)) {
			return null
		}

		return valuesText(singularKey || 'datasets.filters.pretty.date.inAny.singular',
			multipleOrKey || 'datasets.filters.pretty.date.inAny', labels)
	}
}

/**
 * Based on the update type, returns either the value or equation for a date
 * filter.
 */
function getDateValue(filter, field, num, options) {
	if (filter[`periodId${num}`]) {
		return periodLabel(filter, filter[`periodId${num}`])
	} else if (!_.isNil(filter[`periodDelta${num}`])) {
		return getPeriodDeltaText(filter, num, options)
	} else if (filter[`updateType${num}`] === 'calculated') {
		return filter[`equation${num}`]
	} else {
		const value =  filter[`date${num}`] ||
			filter[`dateTime${num}`] ||
			filter[`time${num}`]
		// 35379: Only applicable for date-time data type
		const dataType = _getDateTimeFilterDataType(filter)
		const timeZone = field.timeZone
		const timeFormat = momentHelper.getTimeFormat(field.timeDisplayType)
		return formatValue({ dataType, value, timeZone, timeFormat })
	}
}

function _getDateTimeFilterDataType(filter) {
	if (filter.date1) {
		return DATE
	} else if (filter.time1) {
		return TIME
	} else if (filter.dateTime1) {
		return DATE_TIME
	}
}

// returns the label for the period referenced by the filter's periodDeltaN property
// (N is 1 or 2)
function getPeriodDeltaText (filter, oneOrTwo, options) {
	let deltaProp = 'periodDelta' + oneOrTwo

	if (!options.resolvePeriodDeltas) {
		return relativeLabel(filter[deltaProp])
	}

	let currentPeriod = calendars.periodsById[globals.periodId]
	if (filter.calendarId && currentPeriod.calendar.id !== filter.calendarId) {
		// if the filter specified a calendar and it's not the same as the current calendar,
		// we need to translate the current period into the specified calendar before getting
		// the relative period
		let referencePeriod = calendars.correspondingPeriod(filter.calendarId, currentPeriod.id)
		return calendars.safeRelativePeriod(referencePeriod.id, filter[deltaProp]).label
	} else {
		let period = calendars.safeRelativePeriod(globals.periodId, filter[deltaProp])
		return period.label
	}
}

/**
 * returns the label of the relative calendar referenced in a filter
 */
function getCalendarLabel(filter) {
	// Calendar Type to display. (i.e. "Monthly", "Yearly", etc..)
	let calendarLabel = ''
	let calendar = calendars.calendarsById[filter.calendarId]
	let period = filter.periodId1 && calendars.periodsById[filter.periodId1]
	// #39706 don't display a calendar label if this is a specific date filter
	let isCalendarPeriodFilter = !_.isNil(filter.calendarId)
		|| !_.isNil(filter.periodId1) || !_.isNil(filter.periodDelta1)
	if (_.includes(['between-date-time', 'after', 'before'], filter.operator)
		&& isCalendarPeriodFilter) {
		if (filter.calendarId && calendar) {
			calendarLabel = calendar.label
		} else if (filter.periodId1 && period) {
			calendarLabel = period.calendar.label
		} else {
			calendarLabel = i18n.get('charts.chart.currentCalendar')
		}
		calendarLabel = ` (${calendarLabel})`
	}
	return calendarLabel
}

function periodLabel(filter, periodId) {
	let period = calendars.periodsById[periodId]
	if (!period) {
		// it's gross to have a side-effect like this, but the
		// alternative would be more code
		filter.valid = false
		return i18n.get('datasets.periodDeleted')
	}
	return period.label
}

function rollupFilterText(dataset, filter, field, treeItemsById, singleKey, multipleKey, options = {}) {
	const {operator} = filter

	if (operator === 'in-another-tree' || operator === 'not-in-another-tree') {
		return _anotherTreeRollupText(dataset, filter, singleKey)
	}

	let values
	if (options.showBuiltInRollupTreeGroupLabel &&
		dataset.isFilterBuiltInRollupTemplateFilter(filter)) {
		values = _builtInFilterValues(dataset, filter)
	} else {
		values = _rollupFilterValues(dataset, filter, field, treeItemsById)
	}

	if (_.isEmpty(values)) {
		return null
	}

	let defaultKeys = keysForIsAny(filter.operator)
	return valuesText(singleKey || defaultKeys.singleKey, multipleKey || defaultKeys.multipleKey, values, null, true)
}

function isPermissionFilter(filter, treeId) {
	const rollupFilterSubType = _.get(filter, `rollupIsAnyFilters.${treeId}`)
	return !!(filter.permissionId ||
		// #35465 filter.isPermissionFilter is a flag used with filters that haven't
		// been saved to the DB yet that indicates that they are permission filters
		// (since unsaved permissions won't have a permissionId yet)
		filter.isPermissionFilter ||
		rollupFilterSubType === 'item-only' ||
		rollupFilterSubType == 'rollups-only')
}

/**
 * @param {DatasetFilter} filter
 *
 * @param {String} label describes the rollup filter
 * (e.g. The tree node label, OR `assigned tree items for {rollupTreeGroupLabel}`)
 *
 * @param {String|Number} treeId If the treeId is known by caller it can be provided,
 * otherwise treeIds[0] will be used (e.g. built-in rollup filters)
 *
 * @returns {String} example:
 * "<span class="highlight">first tree</span>, rollup totals, and descendants"
 */
function _rollupTreeText(filter, label, treeId) {
	// always only single tree id for built-ins
	// Need to read from filter because it may not be persisted yet on the principal
	treeId = treeId || _.get(filter, 'treeIds[0]')
	const rollupFilterSubType = filter.rollupIsAnyFilters[treeId]
	let filterKey
	// handle filters created before the fix for #48380
	if (rollupFilterSubType === 'item-and-rollups') {
		filterKey = 'datasets.rollupPermissions.itemAndRollups'
	} else {
		let isAnyFilters = getRollupIsAnyFilters(isPermissionFilter(filter, treeId))
		filterKey = _.find(isAnyFilters, filterOption =>
			filterOption.id == rollupFilterSubType
		).key
	}
	return i18n.get(`${filterKey}.withArg`, label)
}

function _anotherTreeRollupText(dataset, filter, singleKey) {
	if (_.isNil(filter.anotherTreeFieldId)) {
		return null
	}

	var label = dataset.getFieldLabel(filter.anotherTreeFieldId)
	if (filter.anotherTreeDescendants) {
		label = i18n.get('admin.groupsByOrganization.viewOrgWithDescendants', label)
	}
	if (!singleKey) {
		singleKey = filter.operator === 'in-another-tree' ?
			'report.filter.pretty.numeric.equals' :
			'datasets.filters.pretty.isNot'
	}
	return i18n.get(singleKey, label)
}

/**
 * The filter label for a built-in rollup filter for a tree template.
 * e.g. 'assigned tree items for [Template Label], rollup totals, and descendants`
 */
function _builtInFilterValues(dataset, filter) {
	const principal = dataset.getPrincipalForFilter(filter) || {}
	const label = i18n.get(`datasets.filters.pretty.text.eachItemFor`,
		highlight(principal.rollupTreeGroupLabel ||
			filter.rollupTreeGroupLabel)) // see permissions-panel.js#addPrincipal
	return [_rollupTreeText(filter, label)]
}

function _rollupFilterValues(dataset, filter, field, treeItemsById) {
	var keyDisplay = new KeyDisplay({
		model: dataset,
		filter,
		field,
		treeItemsById
	})
	return _(filter.rollupIsAnyFilters)
		.keys()
		.map(treeId => {
			const label = highlight(keyDisplay.labelAndKeys(treeId))
			return _rollupTreeText(filter, label, treeId)
		})
		.sortBy(label => label ? $(label).text().toLowerCase() : '')
		.value()
}

function containsMessage(operator, values, filterOn) {
	// I'm not handling when there are no values because that's not something
	// datasets would support
	if (_.isEmpty(values)) {
		return
	}

	var singleKey = operator === 'contains-any' || operator === 'contains-all' ?
		'datasets.filters.pretty.text.contains1' :
		'datasets.filters.pretty.text.doesNotContain1'
	var multipleKey
	if (operator === 'contains-any') {
		multipleKey = 'datasets.filters.pretty.text.containsAny'
	}
	if (operator === 'contains-all') {
		multipleKey = 'datasets.filters.pretty.text.containsAll'
	}
	if (operator === 'does-not-contain-any') {
		multipleKey = 'datasets.filters.pretty.text.doesNotContainAny'
	}
	if (operator === 'does-not-contain-all') {
		multipleKey = 'datasets.filters.pretty.text.doesNotContainAll'
	}
	return valuesText(singleKey, multipleKey, values, filterOn)
}

/**
 * Constructs a string like "is C", "is C or E" or "is C, E, or O".
 *
 * @param multipleKey
 * 		the key in the message resources
 * @param values
 * 		the values to put in the list
 * @param {Boolean} isHighlighted
 * 		whether the value is already highlighted
 */
function valuesText(singleKey, multipleKey, values, filterOn, isHighlighted, extraI18nArgs = []) {

	// We don't want to update the values array directly because other sections need that
	// and will cause cause unwanted html showing (like tags in the add/edit filter dialog)
	// Instead of editing the array directly, copy the array and edit that.
	var filterValues = _.clone(values)

	if (_.isNil(filterValues) || _.isEmpty(filterValues)) {
		return null
	}

	function transformValue(value) {
		if (value == constants.BLANK) {
			return highlight(i18n.get('datasets.filters.pretty.blank'))
		} else if (isHighlighted) {
			return value
		} else {
			return highlight(value, {preserveLineBreaks: true})
		}
	}

	// handle a single item, e.g. "is C"
	if (filterValues.length === 1) {
		var singleValue = `<span class="filter-group">${transformValue(values[0])}</span>`
		return filterOnCheck(filterOn, i18n.get(singleKey, singleValue, ...extraI18nArgs))
	}

	// handle multiple items, e.g. "is C or E" or "is C, E, or O"

	// Highlight each value so its easier to differentiate between them.
	_.each(filterValues, function(value, index) {
		filterValues[index] = transformValue(value)
	})

	var firstArg = _.join(_.dropRight(filterValues, 1), ', ')
	// add Oxford comma when there are 3 or more
	if (filterValues.length >= 3) {
		firstArg += ','
	}
	var firstValues = '<span class="filter-group">' + firstArg + '</span>'
	var lastValue = '<span class="filter-group">' + _.last(filterValues) + '</span>'
	return filterOnCheck(filterOn, i18n.get(multipleKey, firstValues, lastValue, ...extraI18nArgs))
}

/**
 * Checks to see if there is a "filter on" value needed and applies it if it is.
 */
function filterOnCheck(filterOn, values) {
	let filterOnMessageCodes = {
		'name': 'common.name',
		'key': 'datasets.trees.key',
		'key-or-name': 'datasets.trees.key.orName',
		'key-and-name': 'datasets.trees.key.andName'
	}

	if (_.isNil(filterOn)) {
		return values
	} else {
		return i18n.get('datasets.filterOnValues', i18n.get(filterOnMessageCodes[filterOn]), values)
	}
}

const CHARACTER_LENGTH_OPERATORS = ['character-length-less-than',
	'character-length-greater-than', 'character-length-between']
/**
 * Similar to filterText(), but leave out the operator text. For example,
 * instead of "between 1 and 10", returns "1 and 10"
 *
 * @param {Dataset} dataset
 *
 * @param {boolean} [options.showCalendarLabel=false] Pass true to display
 * the calendar label for relative transformations. FilterText does this by
 * default, but there are situations here where we don't want this information
 */
function noOperator(dataset, filter, field, treeItemsById, options = {}) {
	// filterOn is only used in the "long form" filter text, so just omit it
	// from the filter when doing the shorter "no operator" text
	filter = _.omit(filter, 'filterOn')

	let op = filter.operator

	if (isSimpleOperator(field, op)) {
		// #45972 if the filter operator dropdown isn't being shown, then we
		// need to show the operator text, or it'll just be blank
		return options.hiddenFilterType
			? commonFilterText(dataset, filter, field)
			: null
	}

	if (op === 'in-the-range') {
		if (_.isEmpty(filter.rangeIds)) {
			return null
		}

		return valuesText('common.identity',
			'common.or2', rangeValues(dataset, filter, field))
	}

	let dataType = field.get ? field.get('dataType') : field.dataType

	if (_.includes(beginsEndsWithOperators, filter.operator) ||
		'matches-regular-expression' == filter.operator) {
		return beginsEndsWith('common.identity', filter)
	}

	if (dataType === YES_NO) {
		if (isTextBadgeOperator(filter.operator)) {
			return buildTextFilter(dataset, filter, field)
		}
		// #55465 handle yes/no fields with an operator that uses numeric value(s)
		if (isNumericOperator(filter.operator)) {
			return numberFilterText(dataset, filter, field, 'common.identity', 'common.and2')
		}
		return yesNoFilterText(filter, field, false)
	}

	if (isNumericDataType(dataType) || _.includes(CHARACTER_LENGTH_OPERATORS, filter.operator)) {
		if (isTextBadgeOperator(filter.operator)) {
			return buildTextFilter(dataset, filter, field)
		}
		// for is-any and is-not-any, we can just use the text logic
		if (filter.operator === 'is-any' || filter.operator === 'is-not-any') {
			// #35528 unless it's a percent - which is stored as a decimal, and
			// requires extra formatting
			if (dataType !== 'percent') {
				dataType = TEXT
			}
		} else {
			let doubleArgCode = options.hiddenFilterType ? 'datasets.filters.pretty.to' : 'common.and2'
			return numberFilterText(dataset, filter, field, 'common.identity', doubleArgCode)
		}
	}

	if (dataType === TEXT || dataType === CLUSTER) {
		let values = (filter.values || []).concat(filter.equations || [])
		let multipleKey = _.includes(AND_OPERATORS, op) ? 'common.and2' : 'common.or2'
		return textFilterText(dataset, filter, field, {
			singleKey: 'common.identity',
			multipleKey
		})
	}

	if (isDateTimeDataType(dataType) || dataType === CALENDAR_PERIOD) {
		// #55503 for dateTime fields, there are some filter operators that
		// should be treated as text so the filter value(s) will be displayed.
		// In addition to the TEXT_BADGE_OPERATORS, the "is-any" and "is-not-any"
		// operators should be treated as text as well.
		if (isTextBadgeOperator(filter.operator) ||
			_.includes(['is-any', 'is-not-any'], filter.operator)) {
			return buildTextFilter(dataset, filter, field)
		}
		let multipleAndKey = options.hiddenFilterType ? 'datasets.filters.pretty.to' : 'common.and2'
		let value = dateFilterText(filter, field, 'common.identity', multipleAndKey, 'common.or2', options)

		// #53155 if the value is null, don't add the calendar label, making it
		// a string
		if (options.showCalendarLabel && !_.isNil(value)) {
			value += getCalendarLabel(filter)
		}
		return value
	}

	if (isGeographicOrRollup(dataType)) {
		if (_.includes(['contains-all', 'contains-any', 'does-not-contain-all', 'does-not-contain-any'], op)) {
			let multipleCode = _.includes(AND_OPERATORS, op) ? 'common.and2' : 'common.or2'
			return valuesText('common.identity', multipleCode, filter.values)
		} else {
			return rollupFilterText(dataset, filter, field, treeItemsById, 'common.identity', 'common.or2', options)
		}
	}

	if (dataType === 'percent') {
		let values = (filter.values || []).map(value => formatValue({
			value, dataType: 'percent'
		}))
		let multipleCode = _.includes(AND_OPERATORS, op) ? 'common.and2' : 'common.or2'
		return valuesText('common.identity', multipleCode, values)
	}
}

const AND_OPERATORS = [ 'contains-all', 'does-not-contain-all' ]
const SIMPLE_OPERATORS = [ 'is-blank', 'not-blank', 'not-in-any-period', 'not-in-any-range' ]
/** filter operators that use badges when selecting values and may need
 * to use textFilterText when formatting a filter summary */
const TEXT_BADGE_OPERATORS = [ 'contains-any', 'contains-all',
	'does-not-contain-any', 'does-not-contain-all',
	'character-length-is-any', 'character-length-is-not-any']
/** filter operators that accept numeric values */
const NUMERIC_OPERATORS = [ 'less-than', 'less-than-or-equals',
	'greater-than', 'greater-than-or-equals', 'between-number',
	'character-length-less-than', 'character-length-greater-than',
	'character-length-between' ]

/**
 * Returns whether the operator needs no extra information for comparison. For example, "is-blank"
 * doesn't need any extra values, whereas "begins-with" needs a String for its comparison
 */
function isSimpleOperator (field, op) {
	return _.includes(SIMPLE_OPERATORS, op)
}

/**
 * Returns true if the operator can be considered a text badge operator
 * (ex, "Contains any", "Does not contain any", etc.)
 */
function isTextBadgeOperator(operator) {
	return _.includes(TEXT_BADGE_OPERATORS, operator)
}

/**
 * Returns true if the operator is one that accepts numeric values
 * (ex, "Less than", "Greater than", "Character Length Between", etc.)
 */
function isNumericOperator(operator) {
	return _.includes(NUMERIC_OPERATORS, operator)
}

/**
 * Returns a filter description for operators that can be
 * handled as text (ex. "contains any", "does not contain any", etc.)
 * This is to allow the display of the filter values for non-text
 * datatypes (ie. numeric, yes/no, etc.) #55465
 */
function buildTextFilter(dataset, filter, field) {
	let multipleKey = _.includes(AND_OPERATORS, filter.operator) ? 'common.and2' : 'common.or2'
	return textFilterText(dataset, filter, field, {
		singleKey: 'common.identity',
		multipleKey
	})
}

_.extend(filterText, {
	noOperator,
	isSimpleOperator,
	isPermissionFilter,
	valuesText
})
export default filterText
export { isSimpleOperator }
export { isPermissionFilter }
export { valuesText }
filterText.__filename__ = 'datasets/filters/filter-text.js'
